import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { copy } from '../../config/copy';
import { useSession } from '../../stores/session';
import { favorites, playlists, type Playlist } from '../library/api';
import { Artwork } from './Artwork';

/**
 * Favourites and playlists, down the left of the dashboard.
 *
 * ## Why this exists when the dock already has a Library
 *
 * They answer the same question at different moments. The dock's block is a
 * launcher - reachable from any page, opened deliberately, closed again. This is
 * a surface: it is simply there while somebody is deciding what to listen to,
 * which is the entire purpose of the page it sits on.
 *
 * Deliberately a decision rather than a duplication that crept in. The other
 * option was for the library to become the app's left rail on every page, the
 * way Spotify does it, and for the dock's block to retire into it - which is
 * cleaner and was not what was wanted here.
 *
 * ## The same queries as the dock
 *
 * `['favorites']` and `['playlists', username]`, so a track favourited in one
 * appears in the other without a refresh, and neither can show a list the other
 * disagrees with.
 *
 * ## Not on a phone
 *
 * The rails are hidden below `xl` and the middle column becomes the page. Three
 * columns on a narrow screen is one column of content and two of noise, and the
 * dock's Library block is still there for anybody who wants the lists.
 */
export function LibraryRail() {
  const me = useSession((state) => state.user);

  const { data: liked } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => favorites(),
    enabled: Boolean(me),
  });

  const { data: lists } = useQuery({
    queryKey: ['playlists', me?.username],
    queryFn: () => playlists.mine(me!.username),
    enabled: Boolean(me?.username),
  });

  if (!me) return null;

  const tracks = liked?.items ?? [];
  const collections = lists?.items ?? [];

  return (
    <aside className="flex flex-col gap-8" data-library-rail>
      <section aria-labelledby="rail-favorites">
        <div className="mb-3 flex items-baseline justify-between gap-3 border-b border-line pb-2">
          <h2 id="rail-favorites" className="meta-caps text-faint">
            {copy['noun.like.plural']}
          </h2>
          <Link to="/favorites" className="font-mono text-[10px] text-soft hover:text-ink">
            all →
          </Link>
        </div>

        {tracks.length === 0 ? (
          <p className="text-xs text-faint">
            Nothing saved yet. The heart on any track puts it here.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {tracks.slice(0, 6).map((track) => (
              <li key={track.id}>
                <Link
                  to={`/tracks/${track.id}`}
                  className="flex items-center gap-2.5 rounded-[var(--radius-control)] p-1.5 transition-colors hover:bg-raised"
                >
                  <Artwork
                    track={{
                      title: track.title,
                      artwork: null,
                      artwork_small: track.artwork_small ?? null,
                    }}
                    className="h-8 w-8 shrink-0 rounded-[var(--radius-chip)]"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] text-ink">{track.title}</span>
                    <span className="block truncate font-mono text-[10px] text-faint">
                      {track.owner?.display_name ?? track.owner?.username ?? ''}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="rail-playlists">
        <div className="mb-3 flex items-baseline justify-between gap-3 border-b border-line pb-2">
          <h2 id="rail-playlists" className="meta-caps text-faint">
            Playlists
          </h2>
        </div>

        {collections.length === 0 ? (
          <p className="text-xs text-faint">
            No playlists yet. The plus on the player bar starts one.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {collections.slice(0, 8).map((list: Playlist) => (
              <li key={list.id}>
                <Link
                  to={`/albums/${list.id}`}
                  className="flex items-baseline justify-between gap-2 rounded-[var(--radius-control)] px-1.5 py-1.5 transition-colors hover:bg-raised"
                >
                  <span className="min-w-0 truncate text-[13px] text-ink">{list.title}</span>
                  <span className="shrink-0 font-mono text-[10px] text-faint">
                    {list.track_count}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </aside>
  );
}
